import { computed, Injectable, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { LocalChatService, ChatResponse } from '../../services/local-chat.service';
import { HelpFaqSelectors } from '../../store/selectors';
import { Store } from '@ngrx/store';

import {
  buildConversationContext,
  buildFollowUpChips,
  CATEGORY_REENTRY_MESSAGE,
  CategorySnapshot,
  ChatBubble,
  ChatChip,
  ChatItem,
  classifyChip,
  CLOSE_CHIP_ID,
  createDisplayBubble,
  FailedRequest,
  getCategoryLabel,
  hasRemainingChildren,
  mapOptionsToChips,
  RETRY_CHIP_ID,
  TYPING_ITEM,
  WidgetState,
} from './chat-conversation.logic';

interface ChatState {
  widgetState: WidgetState;
  isLoading: boolean;
  initialized: boolean;
  items: ChatItem[];
  chips: ChatChip[];
  rootChips: ChatChip[];
  selectedRootNodeId: string;
  rootNodeTargetId: string;
  visitedNodeIds: string[];
  childNodeCount: number;
  excludedNodeCount: number;
  currentNodeId: string;
  lastFailedRequest: FailedRequest | null;
  categoryHistories: Record<string, CategorySnapshot>;
  lastVisitedNodeId: string;
}

const initial: ChatState = {
  widgetState: 'closed',
  isLoading: false,
  initialized: false,
  items: [],
  chips: [],
  rootChips: [],
  selectedRootNodeId: '',
  rootNodeTargetId: '',
  visitedNodeIds: [],
  childNodeCount: 0,
  excludedNodeCount: 0,
  currentNodeId: '',
  lastFailedRequest: null,
  categoryHistories: {},
  lastVisitedNodeId: '',
};

@Injectable()
export class ChatConversationStore {
  private readonly _state = signal<ChatState>({ ...initial });

  // ── Public readonly signals ──
  readonly widgetState = computed(() => this._state().widgetState);
  readonly isLoading = computed(() => this._state().isLoading);
  readonly items = computed(() => this._state().items);
  readonly chips = computed(() => this._state().chips);
  readonly rootChips = computed(() => this._state().rootChips);
  readonly selectedRootNodeId = computed(() => this._state().selectedRootNodeId);
  readonly chipsDisabled = computed(() => this._state().isLoading);
  readonly conversationContext = computed(() =>
    buildConversationContext(this._state().items)
  );
  readonly lastFailedRequest = computed(() => this._state().lastFailedRequest);
  readonly childNodeCount = computed(() => this._state().childNodeCount);
  readonly excludedNodeCount = computed(() => this._state().excludedNodeCount);
  readonly visitedNodeIds = computed(() => this._state().visitedNodeIds);
  readonly rootNodeTargetId = computed(() => this._state().rootNodeTargetId);
  readonly categoryHistories = computed(() => this._state().categoryHistories);
  readonly lastVisitedNodeId = computed(() => this._state().lastVisitedNodeId);

  constructor(
    private readonly chat: LocalChatService,
    private readonly store: Store
  ) {}

  private patch(partial: Partial<ChatState>): void {
    this._state.update((s) => ({ ...s, ...partial }));
  }

  private get s(): ChatState {
    return this._state();
  }

  // ── Helpers ──

  private getExcludedNodeIds(): string[] {
    const excluded: string[] = [];
    let cashToClose = false;
    let isFaqLoaded = false;
    let payoffs: unknown[] = [];
    this.store.select(HelpFaqSelectors.getCashToCloseFromLoanEstimate).pipe(take(1))
      .subscribe(v => cashToClose = v);
    this.store.select(HelpFaqSelectors.getIsFaqLoaded).pipe(take(1))
      .subscribe(v => isFaqLoaded = v);
    this.store.select(HelpFaqSelectors.getPayoffs).pipe(take(1))
      .subscribe(v => payoffs = v);
    if (!cashToClose) {
      excluded.push('fundsdueatclosing');
    }
    if (cashToClose) {
      excluded.push('morethaniowe');
    }
    if (!isFaqLoaded) {
      excluded.push('loaninfo');
    }
    if (payoffs.length === 0) {
      excluded.push('payoffs');
    }
    return excluded;
  }

  private removeTypingItem(): void {
    this.patch({ items: this.s.items.filter((i) => i.kind !== 'typing') });
  }

  private addTypingItem(): void {
    this.patch({ items: [...this.s.items, TYPING_ITEM] });
  }

  // ── Public actions ──

  open(): void {
    this.patch({ widgetState: 'opened' });
    if (!this.s.initialized) {
      this.loadStart();
    }
  }

  close(): void {
    this._state.set({ ...initial });
  }

  minimize(): void {
    this.patch({ widgetState: 'minimized' });
  }

  selectChip(chip: ChatChip): void {
    if (this.s.isLoading) {
      return;
    }

    if (chip.id === RETRY_CHIP_ID) {
      this.retry();
      return;
    }

    if (chip.id === CLOSE_CHIP_ID) {
      this._state.set({ ...initial });
      return;
    }

    if (!chip.targetNodeId) {
      return;
    }

    const intent = classifyChip(chip, this.s.rootNodeTargetId);

    switch (intent) {
      case 'root-reset':
        this.handleRootReset();
        this.dispatchSelect(chip, false);
        break;
      case 'category-change':
        this.enterCategory(chip);
        this.dispatchSelect(chip, false);
        break;
      case 'static-yes':
        this.handleStaticYes(chip);
        break;
      case 'static-no':
        this.handleStaticNo();
        break;
      case 'static-close':
        break;
      default:
        this.handleLeafSelection(chip);
        this.dispatchSelect(chip, false);
        break;
    }
  }

  // ── Private state transitions ──

  private loadStart(): void {
    this.patch({ isLoading: true });
    this.chat.getStartNode().pipe(
      catchError(() => {
        this.handleError({ kind: 'start' });
        return of(null);
      })
    ).subscribe((response) => {
      if (response) {
        this.handleStartResponse(response);
      }
    });
  }

  private dispatchSelect(chip: ChatChip, overrideMessage: boolean): void {
    this.addTypingItem();
    this.patch({ isLoading: true });

    this.chat.selectOption({ nodeId: chip.targetNodeId ?? '' }).pipe(
      catchError(() => {
        this.handleError({ kind: 'select', chip });
        return of(null);
      })
    ).subscribe((response) => {
      if (response) {
        this.handleResponse(response, overrideMessage ? CATEGORY_REENTRY_MESSAGE : undefined);
      }
    });
  }

  private handleStartResponse(response: ChatResponse): void {
    this.patch({
      initialized: true,
      isLoading: false,
      items: [createDisplayBubble('bot', 'Need help with something? Pick a topic to get started.')],
      rootChips: mapOptionsToChips(response.options, 'root', this.s.visitedNodeIds, this.getExcludedNodeIds()),
      chips: mapOptionsToChips(response.options, 'root', this.s.visitedNodeIds, this.getExcludedNodeIds()),
    });
  }

  private handleResponse(response: ChatResponse, messageOverride?: string): void {
    this.removeTypingItem();
    const categoryPrompts: Record<string, string> = {
      signing: 'What would you like to know about signing?',
      loan: 'What would you like to know about loans?',
    };
    const text = messageOverride ?? categoryPrompts[response.currentNodeId] ?? response.currentNodeId;

    this.patch({
      items: [...this.s.items, createDisplayBubble('bot', text)],
      chips: mapOptionsToChips(response.options, response.currentNodeId, this.s.visitedNodeIds, this.getExcludedNodeIds()),
      isLoading: false,
    });

    this.applyFollowUp(response);
  }

  private applyFollowUp(response: ChatResponse): void {
    const parentIsRoot = this.s.rootNodeTargetId === response.currentNodeId;

    if (parentIsRoot) {
      const excludedIds = this.getExcludedNodeIds();
      this.patch({
        childNodeCount: response.options.length,
        excludedNodeCount: excludedIds.filter(id =>
          response.options.some(o => o.targetNodeId === id)).length,
      });
    } else if (hasRemainingChildren(this.s.childNodeCount, this.s.visitedNodeIds.length, this.s.excludedNodeCount)) {
      this.patch({
        items: [...this.s.items, createDisplayBubble('bot', 'Need help with anything else?')],
        chips: buildFollowUpChips(this.s.rootNodeTargetId),
      });
    } else if (this.s.rootNodeTargetId !== '') {
      const categoryLabel = getCategoryLabel(this.s.rootNodeTargetId);
      this.patch({
        items: [...this.s.items, createDisplayBubble('bot', `That's everything for ${categoryLabel}. You can switch topics above or close this chat.`)],
        chips: [],
      });
    }
  }

  private handleError(failed: FailedRequest): void {
    this.removeTypingItem();
    this.patch({
      items: [...this.s.items, createDisplayBubble('bot', 'Sorry, something went wrong. Please try again.')],
      chips: [{ id: RETRY_CHIP_ID, label: 'Try again' }],
      isLoading: false,
      lastFailedRequest: failed,
    });
  }

  private retry(): void {
    const failed = this.s.lastFailedRequest;
    if (!failed) {
      return;
    }
    this.patch({
      lastFailedRequest: null,
      items: this.s.items.filter((i) => !(i.kind === 'bot' && i.text === 'Sorry, something went wrong. Please try again.')),
      chips: [],
    });
    if (failed.kind === 'start') {
      this.loadStart();
    } else {
      this.dispatchSelect(failed.chip, false);
    }
  }

  private enterCategory(chip: ChatChip): void {
    const existing = this.s.categoryHistories[chip.targetNodeId ?? ''];
    if (existing) {
      this.patch({
        rootNodeTargetId: chip.targetNodeId ?? '',
        selectedRootNodeId: chip.id,
        visitedNodeIds: existing.visitedNodeIds,
        childNodeCount: existing.childNodeCount,
        excludedNodeCount: existing.excludedNodeCount,
        chips: existing.chips,
        items: existing.items,
        currentNodeId: existing.currentNodeId,
      });
      this.dispatchSelect(chip, true);
      return;
    }
    this.patch({
      rootNodeTargetId: chip.targetNodeId ?? '',
      selectedRootNodeId: chip.id,
      visitedNodeIds: [],
      excludedNodeCount: 0,
      items: [],
    });
  }

  private handleRootReset(): void {
    this.patch({
      selectedRootNodeId: '',
      visitedNodeIds: [],
      excludedNodeCount: 0,
    });
  }

  private handleStaticYes(chip: ChatChip): void {
    const historyWithoutCurrent = { ...this.s.categoryHistories };
    delete historyWithoutCurrent[this.s.rootNodeTargetId];
    this.patch({
      categoryHistories: historyWithoutCurrent,
      items: [...this.s.items, createDisplayBubble('user', 'Yes')],
    });
    this.dispatchSelect(chip, true);
  }

  private handleStaticNo(): void {
    this.patch({
      chips: [],
      items: [
        ...this.s.items,
        createDisplayBubble('user', 'No'),
        createDisplayBubble('bot', 'Got it! Feel free to open this chat anytime if you have more questions.'),
      ],
    });
    this.patch({ chips: [{ id: CLOSE_CHIP_ID, label: 'Close chat' }] });
  }

  private handleLeafSelection(chip: ChatChip): void {
    this.patch({
      visitedNodeIds: [...this.s.visitedNodeIds, chip.targetNodeId ?? ''],
      lastVisitedNodeId: chip.targetNodeId ?? '',
      items: [...this.s.items, createDisplayBubble('user', chip.label)],
      categoryHistories: {
        ...this.s.categoryHistories,
        [this.s.rootNodeTargetId]: {
          items: this.s.items,
          chips: this.s.chips,
          visitedNodeIds: this.s.visitedNodeIds,
          childNodeCount: this.s.childNodeCount,
          excludedNodeCount: this.s.excludedNodeCount,
          currentNodeId: this.s.currentNodeId,
        },
      },
    });
  }
}
